/**
 * SsTankModelsAdmin — admin-only page for the Sun Stellar SS Tank
 * (Stainless Steel water-storage tank) Sizing Calculator's SKU catalogue
 * (Calculator #3).
 *
 * Mirrors the structure of CommercialHeatPumpModelsAdmin / PoolModelsAdmin:
 * physical product specs (inner-tank grade, sheet thickness, insulation)
 * are read-only — they ship from Sun Stellar Engineering and only Engineering
 * changes them (engine semver bumps when they do). The only field this page
 * mutates is the per-SKU stock status.
 *
 * Per docs/calculators/ss-tank/REFERENCE.html the catalogue is split across:
 *   Standard sizes (8):   500 L · 1,000 L · 1,500 L · 2,000 L · 2,500 L ·
 *                         3,000 L · 4,000 L · 5,000 L
 *   Custom (engineered):  >5,000 L — sheet thickness + insulation type
 *                         (PUF / Rockwool) designed per site.
 *
 * Per the calculator spec, the recommended grade + sheet + insulation
 * combination per SKU is determined by the resolved water tier (T1 / T2 /
 * T3) at calc-time, NOT pre-bound on the SKU. This admin page therefore
 * shows TWO sections:
 *   1. Standard SKU catalogue — 8 size rows × stock-status toggle.
 *      Pricing column is a placeholder ("Wired post-launch") per §11.4.
 *   2. Tier-spec matrix — 3 read-only rows showing the (recommended +
 *      premium upsell) spec for each water tier. This mirrors the
 *      `getSpec()` table in REFERENCE.html lines 514–535. Editing this
 *      requires an Engineering semver bump, so it's not interactive here —
 *      it's surfaced read-only so admins can verify what the calculator
 *      will recommend.
 *
 * Catalog source: backend/src/modules/sun-stellar-ss-tank-calc/catalog.ts
 * (ST-B owns the module). The page does NOT pull from a DB table — it
 * pulls from the backend's GET /v1/ss-tank/catalog/products endpoint.
 * Until that endpoint ships, the table renders the in-code values and the
 * stock toggle calls
 *   PATCH /v1/ss-tank/catalog/products/:sizeL
 * If the endpoint is not yet wired, the toggle surfaces a friendly error
 * but the visual state still flips locally so QA can preview UX.
 *
 * Track D — Calculator #3 (ST-E, May 2026).
 *
 * TODO (parent integration): add `<Route path="/admin/ss-tank-models"
 *   element={<SsTankModelsAdmin />} />` to App.tsx (next to the existing
 *   `/admin/pool-models` and `/admin/commercial-heat-pump-models` routes)
 *   once ST-A/B/C land. ST-C owns the App.tsx edit — leaving it alone here.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, AlertTriangle, Check, Info } from 'lucide-react';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';

type StockStatus = 'available' | 'low_stock' | 'made_to_order';

interface CatalogProductRow {
  /** Tank capacity in litres — also the natural ID (e.g. 1500). */
  size_l: number;
  /** Display label, e.g. "1,500 L". */
  label: string;
  /** Optional INR pricing — wired post-launch per §11.4. */
  price_inr?: number | null;
  /** Admin-controlled stock status. */
  stock_status: StockStatus;
}

interface TierSpecRow {
  /** Tier key — t1 / t2 / t3. */
  tier: 't1' | 't2' | 't3';
  /** Human-readable tier label. */
  label: string;
  /** Inner tank grade for the recommended config. */
  rec_grade: string;
  /** Sheet thickness for the recommended config. */
  rec_thickness: string;
  /** Insulation for the recommended config. */
  rec_insulation: string;
  /** Premium upsell label (what the upsell adds). */
  prem_label: string;
  /** Premium upsell — grade. */
  prem_grade: string;
  /** Premium upsell — thickness. */
  prem_thickness: string;
  /** Premium upsell — insulation. */
  prem_insulation: string;
}

interface CatalogResponse {
  /** Standard sizes — admin-editable stock status. */
  standard: CatalogProductRow[];
  /** Tier-spec matrix — read-only, shown for transparency. */
  tier_specs: TierSpecRow[];
  /** Custom-flow threshold (anything above this is engineered to size). */
  custom_threshold_l: number;
}

// ─── In-code fallback (mirrors backend/src/modules/.../catalog.ts) ─────
// Used only if the GET /catalog/products endpoint is not yet wired by ST-B.
// Numbers MUST stay in sync with REFERENCE.html `CONFIG.standardSizes` and
// `getSpec()`. If you change one here, change the other; otherwise the
// page lies about what the engine does.
const FALLBACK_CATALOG: CatalogResponse = {
  standard: [
    { size_l: 500,   label: '500 L',   price_inr: null, stock_status: 'available' },
    { size_l: 1000,  label: '1,000 L', price_inr: null, stock_status: 'available' },
    { size_l: 1500,  label: '1,500 L', price_inr: null, stock_status: 'available' },
    { size_l: 2000,  label: '2,000 L', price_inr: null, stock_status: 'available' },
    { size_l: 2500,  label: '2,500 L', price_inr: null, stock_status: 'available' },
    { size_l: 3000,  label: '3,000 L', price_inr: null, stock_status: 'available' },
    { size_l: 4000,  label: '4,000 L', price_inr: null, stock_status: 'available' },
    { size_l: 5000,  label: '5,000 L', price_inr: null, stock_status: 'available' },
  ],
  tier_specs: [
    {
      tier: 't1',
      label: 'Tier 1 — Soft water (TDS up to 300 ppm)',
      rec_grade: 'SS 316L',
      rec_thickness: '1.0 mm',
      rec_insulation: '60 mm PUF',
      prem_label: 'Sheet upgrade — 1.5 mm',
      prem_grade: 'SS 316L',
      prem_thickness: '1.5 mm',
      prem_insulation: '60 mm PUF',
    },
    {
      tier: 't2',
      label: 'Tier 2 — Hard water (TDS up to 600 ppm)',
      rec_grade: 'SS 316L',
      rec_thickness: '1.5 mm',
      rec_insulation: '60 mm PUF',
      prem_label: '100 mm insulation',
      prem_grade: 'SS 316L',
      prem_thickness: '1.5 mm',
      prem_insulation: '100 mm PUF',
    },
    {
      tier: 't3',
      label: 'Tier 3 — Brackish water (TDS up to 1000 ppm)',
      rec_grade: 'SS 2205 Duplex',
      rec_thickness: '1.5 mm',
      rec_insulation: '60 mm PUF',
      prem_label: '100 mm insulation',
      prem_grade: 'SS 2205 Duplex',
      prem_thickness: '1.5 mm',
      prem_insulation: '100 mm PUF',
    },
  ],
  custom_threshold_l: 5000,
};

const STOCK_OPTIONS: { value: StockStatus; label: string; bg: string; fg: string }[] = [
  { value: 'available',     label: 'IN STOCK',      bg: '#d8f5d8', fg: '#065c0c' },
  { value: 'low_stock',     label: 'LOW STOCK',     bg: '#fff3cd', fg: '#7a5800' },
  { value: 'made_to_order', label: 'MADE TO ORDER', bg: '#fde7d8', fg: '#8a3a08' },
];

type TabKey = 'standard' | 'tier_specs';

const TAB_LABELS: Record<TabKey, { title: string; subtitle: string }> = {
  standard: {
    title: 'Standard catalogue',
    subtitle: '8 sizes · 500 L → 5,000 L · stock-status editable',
  },
  tier_specs: {
    title: 'Water-tier spec matrix',
    subtitle: '3 tiers · grade + thickness + insulation · read-only · Engineering-controlled',
  },
};

export function SsTankModelsAdmin() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = (user?.role?.name ?? '').toLowerCase() === 'admin';

  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [savingSize, setSavingSize] = useState<number | null>(null);
  const [tab, setTab] = useState<TabKey>('standard');

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    api
      .get<CatalogResponse>('/ss-tank/catalog/products')
      .then((r) => setCatalog(r.data))
      .catch((e) => {
        // If the endpoint isn't wired yet (404 or network), fall back to the
        // in-code catalogue so the page is still useful for QA. The toggle
        // will show a friendly error when actually clicked.
        const status = e?.response?.status;
        if (status === 404 || status === undefined) {
          setCatalog(FALLBACK_CATALOG);
          setWarning(
            'Backend catalog endpoint not yet wired. Showing in-code values. ' +
              'Stock toggle will fail until ST-B ships GET/PATCH ' +
              '/ss-tank/catalog/products.'
          );
        } else {
          setError(e?.response?.data?.error ?? 'Could not load catalog.');
        }
      })
      .finally(() => setLoading(false));
  }, [isAdmin]);

  async function setStock(sizeL: number, next: StockStatus) {
    if (!catalog) return;
    setSavingSize(sizeL);
    setError(null);
    try {
      const res = await api.patch<CatalogProductRow>(
        `/ss-tank/catalog/products/${sizeL}`,
        { stock_status: next }
      );
      const updated: CatalogProductRow = res.data;
      setCatalog((prev) => prev && {
        ...prev,
        standard: prev.standard.map((r) => (r.size_l === sizeL ? updated : r)),
      });
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 404 || status === undefined) {
        // Endpoint not yet implemented. Flip locally so QA can preview UX.
        setCatalog((prev) => prev && {
          ...prev,
          standard: prev.standard.map((r) =>
            r.size_l === sizeL ? { ...r, stock_status: next } : r
          ),
        });
        setError(
          `PATCH /ss-tank/catalog/products/${sizeL} not yet wired. ` +
            'Local state updated for preview only — refresh will revert.'
        );
      } else {
        setError(e?.response?.data?.error ?? 'Could not update stock status.');
      }
    } finally {
      setSavingSize(null);
    }
  }

  const standardRows: CatalogProductRow[] = useMemo(() => catalog?.standard ?? [], [catalog]);
  const tierRows: TierSpecRow[] = useMemo(() => catalog?.tier_specs ?? [], [catalog]);

  if (!isAdmin) {
    return (
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '40px 20px' }}>
        <h1 style={{ fontFamily: '"Fraunces", serif', fontSize: 28, fontWeight: 600, color: '#003a6b', margin: 0 }}>
          SS Tank Catalog
        </h1>
        <div style={emptyStateBox}>Admin only.</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '24px 20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontFamily: '"Fraunces", serif', fontSize: 28, fontWeight: 600, color: '#003a6b', margin: 0 }}>
          SS Tank Catalog
        </h1>
        <span style={catalogSourcePill}>
          <Info size={12} /> Catalog source: in-code (catalog.ts)
        </span>
      </div>

      <p style={{ color: '#666', marginTop: 4, lineHeight: 1.5 }}>
        Standard SS tank sizes for the Sun Stellar Tank Sizing Calculator. Capacities,
        grades, sheet thickness and insulation are physical product specs and ship
        from Sun Stellar Engineering — they're read-only here. The only editable
        field is per-SKU stock status; flip it as inventory changes and the engine
        will recommend the next-larger size or a multi-tank split when a SKU is
        out of stock. Anything above {(catalog?.custom_threshold_l ?? 5000).toLocaleString('en-IN')} L is
        engineered-to-size (sheet thickness + insulation type designed per site).
        Pricing is wired post-launch (per §11.4 — applied at quote generation).
      </p>

      {warning && (
        <div style={{ ...alertBox, background: '#fff8e1', color: '#7a5800' }}>
          <AlertTriangle size={16} /> {warning}
        </div>
      )}

      {error && (
        <div style={{ ...alertBox, background: '#fde7e7', color: '#8b2222' }}>
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* Tabs */}
      <div role="tablist" style={tabRow}>
        {(Object.keys(TAB_LABELS) as TabKey[]).map((key) => {
          const meta = TAB_LABELS[key];
          const count =
            key === 'standard' ? (catalog?.standard?.length ?? 0)
            : (catalog?.tier_specs?.length ?? 0);
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(key)}
              style={{
                ...tabButton,
                background: active ? '#003a6b' : '#f3f8ff',
                color: active ? '#fff' : '#003a6b',
                borderBottomColor: active ? '#003a6b' : 'transparent',
              }}
            >
              <span style={{ fontWeight: 600 }}>{meta.title}</span>
              <span style={{ fontSize: 11, opacity: 0.85 }}>({count})</span>
            </button>
          );
        })}
      </div>

      <p style={{ color: '#777', fontSize: 12, marginTop: 4, marginBottom: 12 }}>
        {TAB_LABELS[tab].subtitle}
      </p>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Loader2 className="animate-spin" />
        </div>
      ) : tab === 'standard' ? (
        <table style={tableStyle}>
          <thead>
            <tr style={{ background: '#f3f8ff', textAlign: 'left' }}>
              <th style={th}>Capacity</th>
              <th style={th}>Litres</th>
              <th style={th}>Price (INR)</th>
              <th style={th}>Stock status</th>
            </tr>
          </thead>
          <tbody>
            {standardRows.map((r) => {
              const isSaving = savingSize === r.size_l;
              const opt = STOCK_OPTIONS.find((s) => s.value === r.stock_status) ?? STOCK_OPTIONS[0]!;
              return (
                <tr key={r.size_l} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={td}><strong>{r.label}</strong></td>
                  <td style={td}>{r.size_l.toLocaleString('en-IN')}</td>
                  <td style={{ ...td, color: '#999', fontStyle: 'italic', fontSize: 12 }}>
                    Wired post-launch
                  </td>
                  <td style={td}>
                    <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                      <select
                        value={r.stock_status}
                        onChange={(e) => setStock(r.size_l, e.target.value as StockStatus)}
                        disabled={isSaving}
                        aria-label={`Stock status for ${r.label}`}
                        style={{
                          padding: '4px 8px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 600,
                          border: 0,
                          cursor: isSaving ? 'wait' : 'pointer',
                          background: opt.bg,
                          color: opt.fg,
                        }}
                      >
                        {STOCK_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                      {isSaving ? (
                        <Loader2 size={12} className="animate-spin" color="#777" />
                      ) : (
                        <Check size={12} color="#999" />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr style={{ background: '#f3f8ff', textAlign: 'left' }}>
              <th style={th}>Tier</th>
              <th style={th}>Recommended grade</th>
              <th style={th}>Rec. thickness</th>
              <th style={th}>Rec. insulation</th>
              <th style={th}>Premium upsell</th>
              <th style={th}>Prem. grade</th>
              <th style={th}>Prem. thickness</th>
              <th style={th}>Prem. insulation</th>
            </tr>
          </thead>
          <tbody>
            {tierRows.map((r) => (
              <tr key={r.tier} style={{ borderBottom: '1px solid #eee' }}>
                <td style={td}><strong>{r.label}</strong></td>
                <td style={td}>{r.rec_grade}</td>
                <td style={td}>{r.rec_thickness}</td>
                <td style={td}>{r.rec_insulation}</td>
                <td style={{ ...td, color: '#003a6b', fontWeight: 500 }}>{r.prem_label}</td>
                <td style={td}>{r.prem_grade}</td>
                <td style={td}>{r.prem_thickness}</td>
                <td style={td}>{r.prem_insulation}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────
const th: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '2px solid #ddd',
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 13 };
const tableStyle: React.CSSProperties = {
  width: '100%',
  marginTop: 8,
  borderCollapse: 'collapse',
};
const tabRow: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  marginTop: 16,
  borderBottom: '1px solid #ddd',
};
const tabButton: React.CSSProperties = {
  padding: '8px 16px',
  border: 0,
  borderBottom: '3px solid transparent',
  borderRadius: '6px 6px 0 0',
  cursor: 'pointer',
  fontSize: 13,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  transition: 'background 0.15s',
};
const alertBox: React.CSSProperties = {
  padding: 12,
  borderRadius: 6,
  marginTop: 12,
  display: 'flex',
  gap: 8,
  alignItems: 'flex-start',
  fontSize: 13,
  lineHeight: 1.4,
};
const catalogSourcePill: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: '#eef4fb',
  color: '#003a6b',
  fontSize: 11,
  fontWeight: 500,
  padding: '4px 10px',
  borderRadius: 999,
};
const emptyStateBox: React.CSSProperties = {
  marginTop: 32,
  padding: 32,
  background: '#f7f7f7',
  border: '1px dashed #ccc',
  borderRadius: 8,
  textAlign: 'center',
  color: '#666',
};
